import { useCallback, useEffect, useMemo, useState } from "react";
import { Modal, Tooltip, TreeSelect, Button, Spin, Empty, Typography } from "antd";
import { QuestionCircleOutlined } from "@ant-design/icons";
import { Toast } from "../../../ui";
import SynchronizationService from "../../../../services/synchronizationService";
import { useTheme } from "../../../../contexts/ThemeContext";

const { Text } = Typography;

// Espelha o enum DefaultFinancialOperation do backend (3 - Domain/Project/Enum.cs).
// A ordem reflete a tela "Configurar categorias financeiras padrão" do Conta Azul.
export const OPERATIONS = [
  {
    id: 0,
    label: "Fretes recebidos",
    help: "Valores recebidos de clientes referentes a fretes em vendas.",
  },
  {
    id: 1,
    label: "Fretes pagos",
    help: "Valores pagos a fornecedores ou transportadoras referentes a fretes.",
  },
  {
    id: 2,
    label: "Impostos retidos em vendas",
    help: "Impostos retidos em notas fiscais de venda (ISS, PIS, COFINS, IRRF, etc.).",
  },
  {
    id: 3,
    label: "Descontos incondicionais obtidos",
    help: "Descontos comerciais obtidos do fornecedor sem condição de pagamento.",
  },
  {
    id: 4,
    label: "Descontos incondicionais concedidos",
    help: "Descontos comerciais concedidos ao cliente sem condição de pagamento.",
  },
  {
    id: 5,
    label: "Descontos financeiros obtidos",
    help: "Descontos obtidos por antecipação ou negociação financeira.",
  },
  {
    id: 6,
    label: "Descontos financeiros concedidos",
    help: "Descontos concedidos por antecipação ou negociação financeira.",
  },
  {
    id: 7,
    label: "Multas recebidas",
    help: "Multas cobradas de clientes em atrasos de pagamento.",
  },
  {
    id: 8,
    label: "Multas pagas",
    help: "Multas pagas a fornecedores em atrasos de pagamento.",
  },
  {
    id: 9,
    label: "Juros recebidos",
    help: "Juros recebidos de clientes ou aplicações financeiras.",
  },
  {
    id: 10,
    label: "Juros pagos",
    help: "Juros pagos a fornecedores, instituições financeiras ou impostos.",
  },
  {
    id: 11,
    label: "Tarifas",
    help: "Tarifas bancárias, taxas de cartão e outros encargos operacionais.",
  },
  {
    id: 12,
    label: "Perdas",
    help: "Perdas estimadas ou reconhecidas em recebíveis ou estoque.",
  },
];

export function normalizeCode(value) {
  if (value === null || value === undefined) return "";
  return String(value).trim();
}

/**
 * Constrói a árvore de categorias para o TreeSelect a partir da lista flat retornada
 * por `/sync/{id}/getCategoryList`. Preserva a hierarquia (CodCategoria <- CodCategoriaSup)
 * e ordena alfabeticamente por descrição em cada nível.
 */
export function buildCategoryTree(categories) {
  const list = (categories || []).filter((c) => c && c.codCategoria);

  const byCode = new Map();
  list.forEach((c) => {
    byCode.set(normalizeCode(c.codCategoria), {
      title: c.descricao || c.codCategoria,
      value: normalizeCode(c.codCategoria),
      key: normalizeCode(c.codCategoria),
      // Permite seleção apenas em nós folha; pais são apenas agrupadores
      selectable: true,
      raw: c,
      children: [],
    });
  });

  const roots = [];
  byCode.forEach((node) => {
    const parentCode = normalizeCode(node.raw.codCategoriaSup);
    if (parentCode && byCode.has(parentCode) && parentCode !== node.value) {
      byCode.get(parentCode).children.push(node);
    } else {
      roots.push(node);
    }
  });

  const sort = (nodes) => {
    nodes.sort((a, b) =>
      String(a.title || "").localeCompare(String(b.title || ""), "pt-BR", {
        sensitivity: "base",
      }),
    );
    nodes.forEach((n) => sort(n.children));
  };
  sort(roots);

  // Marca pais como não selecionáveis quando tiverem filhos (mantém comportamento de
  // árvore: usuário escolhe a categoria mais específica). Categorias sem filhos
  // continuam selecionáveis.
  const markSelectability = (nodes) => {
    nodes.forEach((n) => {
      if (n.children && n.children.length > 0) {
        n.selectable = false;
      }
      markSelectability(n.children || []);
    });
  };
  markSelectability(roots);

  return roots;
}

/**
 * Modal "Configurar categorias financeiras padrão".
 * Permite mapear cada operação financeira fixa (Fretes, Multas, Juros, Tarifas, ...)
 * a uma categoria existente no ERP, espelhando a configuração do Conta Azul V2.
 */
export default function DefaultFinancialCategoriesModal({
  open,
  syncId,
  onClose,
}) {
  const { theme } = useTheme();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  // Map<operationId, { codCategoria: string, id?: string }>
  const [mappings, setMappings] = useState({});

  const treeData = useMemo(() => buildCategoryTree(categories), [categories]);

  const flatByCode = useMemo(() => {
    const map = new Map();
    (categories || []).forEach((c) => {
      const code = normalizeCode(c?.codCategoria);
      if (code) map.set(code, c);
    });
    return map;
  }, [categories]);

  const loadAll = useCallback(async () => {
    if (!syncId) return;
    setLoading(true);
    try {
      const [cats, current] = await Promise.all([
        SynchronizationService.getSynchronizationCategoryList(syncId),
        SynchronizationService.getDefaultFinancialCategories(syncId),
      ]);
      setCategories(Array.isArray(cats) ? cats : []);
      const next = {};
      (current || []).forEach((row) => {
        const op =
          typeof row.operation === "number"
            ? row.operation
            : Number(row.operation);
        if (Number.isFinite(op)) {
          next[op] = {
            id: row.id ?? null,
            codCategoria: normalizeCode(row.codCategoria),
          };
        }
      });
      setMappings(next);
    } catch (e) {
      console.error(e);
      Toast.error("Não foi possível carregar a configuração.");
    } finally {
      setLoading(false);
    }
  }, [syncId]);

  useEffect(() => {
    if (open) {
      loadAll();
    } else {
      setMappings({});
      setCategories([]);
    }
  }, [open, loadAll]);

  const handleChange = useCallback((operationId, codCategoria) => {
    setMappings((prev) => ({
      ...prev,
      [operationId]: {
        ...(prev[operationId] || {}),
        codCategoria: normalizeCode(codCategoria),
      },
    }));
  }, []);

  const handleSave = useCallback(async () => {
    if (!syncId) return;
    setSaving(true);
    try {
      const items = OPERATIONS.map((op) => {
        const current = mappings[op.id] || {};
        return {
          id: current.id ?? null,
          operation: op.id,
          codCategoria: current.codCategoria || "",
          isDeleted: !current.codCategoria,
        };
      });
      await SynchronizationService.upsertDefaultFinancialCategories(
        syncId,
        items,
      );
      Toast.success("Configuração salva com sucesso.");
      onClose?.(true);
    } catch (e) {
      console.error(e);
      Toast.error("Não foi possível salvar a configuração.");
    } finally {
      setSaving(false);
    }
  }, [mappings, onClose, syncId]);

  const renderRow = (op) => {
    const value = mappings[op.id]?.codCategoria || undefined;
    const selectedCat = value ? flatByCode.get(value) : null;
    const displayValue = selectedCat ? value : value || undefined;

    return (
      <div
        key={op.id}
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(220px, 1fr) minmax(260px, 1.2fr)",
          alignItems: "center",
          gap: 16,
          padding: "10px 12px",
          borderBottom: `1px solid ${theme.borderSecondary}`,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <Text style={{ color: theme.textPrimary }}>{op.label}</Text>
          <Tooltip title={op.help}>
            <QuestionCircleOutlined
              style={{ color: theme.textSecondary, fontSize: 14 }}
            />
          </Tooltip>
        </div>
        <TreeSelect
          allowClear
          showSearch
          autoClearSearchValue={false}
          treeDefaultExpandAll={false}
          placeholder={op.label}
          value={displayValue}
          treeData={treeData}
          treeNodeFilterProp="title"
          style={{ width: "100%" }}
          onChange={(val) => handleChange(op.id, val)}
          dropdownStyle={{ maxHeight: 360, overflow: "auto" }}
          notFoundContent={
            loading ? (
              <Spin size="small" />
            ) : (
              <Empty
                description="Nenhuma categoria"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            )
          }
        />
      </div>
    );
  };

  return (
    <Modal
      open={open}
      onCancel={() => onClose?.(false)}
      title="Configurar categorias financeiras padrão"
      width={760}
      destroyOnClose
      maskClosable={false}
      footer={[
        <Button key="cancel" onClick={() => onClose?.(false)} disabled={saving}>
          Cancelar
        </Button>,
        <Button
          key="save"
          type="primary"
          loading={saving}
          disabled={loading}
          onClick={handleSave}
        >
          Salvar
        </Button>,
      ]}
    >
      <div style={{ marginBottom: 12 }}>
        <Text style={{ color: theme.textSecondary }}>
          Vincule cada operação financeira a uma categoria do seu plano de contas.
          Esses vínculos são usados quando a estrutura de DRE segue a configuração
          padrão do Conta Azul.
        </Text>
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(220px, 1fr) minmax(260px, 1.2fr)",
          gap: 16,
          padding: "8px 12px",
          backgroundColor: theme.borderSecondary,
          borderRadius: 6,
          fontWeight: 600,
        }}
      >
        <Text style={{ color: theme.textPrimary }}>Operação financeira</Text>
        <Text style={{ color: theme.textPrimary }}>
          Vincular automaticamente à categoria
        </Text>
      </div>
      <Spin spinning={loading}>
        <div style={{ marginTop: 4 }}>{OPERATIONS.map(renderRow)}</div>
      </Spin>
    </Modal>
  );
}
